import * as React from "react";
import * as ReactDOM from "react-dom";
import { IComboBoxValue } from "../../../models/form";
import { IRuleMourningHall, IMourningHall, ICalendar } from "../../../models/get";
import { Loader, LoaderStyle } from "../../../components/loader";
import { ToolBar, ToolBarItem, ToolBarSeparator } from "../../../components/toolbar";
import { InputSize } from "../../../components/input";
import { TextBox } from "../../../components/textBox";
import { Button, ButtonType, ButtonSize } from "../../../components/button";
import { ComboBox, ComboBoxType, DROPDOWN_TEXT_ITEM_HEIGHT, DropDownTextItem } from "../../../components/comboBox";
import { Alert, AlertType } from "../../../components/alert";

interface IProperties
{
    /**
     * Alle Eingerichteten Trauerhallen.
     */
    mourningHalls: IMourningHall[],

    /**
     * Alle eingerichteten Kalender.
     */
    calendars: ICalendar[],

    /**
     * Angabe, ob das "Dann" Snippet im RuleEditor sichtbar ist.
     */
    isThenVisible: boolean,

    /**
     * Der Wert des "Wenn" Snippets.
     */
    valueWhen: IComboBoxValue<string, number>,

    /**
     * Der Wert des "Dann" Snippets.
     */
    valueThen: IComboBoxValue<string, number>,

    /**
     * Die Werte für die Kalender Snippets.
     */
    valueCalendars: IComboBoxValue<string, number>[],

    /**
     * Alle Serverseitigen Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Wird ausgeführt, sobald sich der Wert des "Dann" Snippets ändert.
     */
    onWhenChanged?: (mourningHall: IMourningHall) => void,

    /**
     * Wird ausgeführt, sobald sich der Wert des "Wenn" Snippets ändert.
     */
    onThenChanged?: (mourningHall: IMourningHall) => void,

    /**
     * Wird ausgeführt, sobald ein Kalender Snippet dem Editor hinzugefügt wird.
     */
    onCalendarAdded?: () => void,

    /**
     * Wird ausgeführt, sobald ein Kalender Snippet dem Editor entfernt wird.
     */
    onCalendarRemoved?: (index: number) => void,

    /**
     * Wird ausgeführt, sobald sich der Wert eines "Kalender" Snippets ändert.
     */
    onCalendarsChanged?: (calendars: ICalendar, index: number) => void,

    /**
     * Wird ausgeführt, sobald das "Dann" Snippet dem Editor hinzugefügt wird.
     */
    onThenAdded?: () => void,

    /**
     * Wird ausgeführt, sobald das "Dann" Snippet dem Editor entfernt wird.
     */
    onThenRemoved?: () => void
}

export default class RulesEditor extends React.Component<IProperties>
{
    /**
     * Erstellt eine neue Instanz der Klasse @see RulesEditor.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props: IProperties)
    {
        super(props);
    }

    /**
     * Wird ausgeführt, sobald das Snippet "Dann" hinzugefügt wird.
     */
    private onAddThenClick = () =>
    {
        const { onThenAdded } = this.props;
        onThenAdded && onThenAdded();
    }

    /**
     * Wird ausgeführt, sobald das Snippet "Kalender" hinzugefügt wird.
     */
    private onAddCalendarClick = () =>
    {
        const { onCalendarAdded } = this.props;
        onCalendarAdded && onCalendarAdded();
    }

    /**
     * Wird ausgeführt, sobald ein Snippet entfernt wird.
     * @param snippetType Der Typ des Snippets.
     * @param index Wenn der Typ ein Kalender Snippet ist, dann ist hier der Index des Snippets.
     */
    private onRemoveSnippet = (snippetType: "calendar" | "then", index: number = 0) =>
    {
        const { valueThen, onThenChanged, onCalendarRemoved, onThenRemoved } = this.props;

        if (snippetType === "calendar")
        {
            onCalendarRemoved && onCalendarRemoved(index);
        }
        else
        {
            if (valueThen != null && valueThen.value != null)
            {
                onThenChanged && onThenChanged(null);
            }

            onThenRemoved && onThenRemoved();
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert der ComboBoxen "Wenn" und "Dann" ändert.
     * @param selectedItem Der ausgewählte Datensatz.
     */
    private onComboBoxChanged = (selectedItem: IMourningHall, name: string) =>
    {
        const { onWhenChanged, onThenChanged } = this.props;

        if (name === "when")
        {
            onWhenChanged && onWhenChanged(selectedItem);
        }
        else if (name === "then")
        {
            onThenChanged && onThenChanged(selectedItem);
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert der Kalender ComboBoxen ändert.
     * @param selectedItem Der ausgewählte Datensatz.
     */
    private onCalendarChanged = (selectedItem: ICalendar, index: number) =>
    {
        const { onCalendarsChanged } = this.props;
        onCalendarsChanged && onCalendarsChanged(selectedItem, index);
    }

    /**
     * Rendert die Buttons für das hinzufügen von Snippets.
     */
    private renderButtons(): JSX.Element
    {
        const { isThenVisible } = this.props;
        const classNames: string[] = ["mh-rule-editor_rule_btn-wrapper"];

        if (isThenVisible)
        {
            classNames.push("mh-rule-editor_rule_btn-wrapper--nopadding");

            return (
                <div className={classNames.join(" ")}>
                    <div
                        className="mh-rule-editor_rule_btn mh-rule-editor_rule_btn--normal"
                        onClick={this.onAddCalendarClick}>
                        <i className="mdi mdi-plus"></i>
                        <span>Kalender</span>
                    </div>
                </div>
            );
        }
        else
        {
            return (
                <div className={classNames.join(" ")}>
                    <div
                        className="mh-rule-editor_rule_btn mh-rule-editor_rule_btn--round"
                        onClick={this.onAddThenClick}>
                        <i className="mdi mdi-plus"></i>
                    </div>
                    <div
                        className="mh-rule-editor_rule_btn mh-rule-editor_rule_btn--normal"
                        onClick={this.onAddCalendarClick}>
                        <i className="mdi mdi-plus"></i>
                        <span>Kalender</span>
                    </div>
                </div>
            );
        }
    }

    /**
     * Rendert das "Wenn" Snippet.
     */
    private renderWhen(): JSX.Element[]
    {
        const { mourningHalls, valueWhen, errors } = this.props;
        const when: JSX.Element[] = [];

        when.push(<div className="mh-rule-editor_rule_header">wenn</div>);
        when.push(
            <div className="mh-rule-editor_rule_snippet">
                <div className="mh-rule-editor_rule_snippet_dot"></div>
                <div className="mh-rule-editor_rule_snippet_text">am selben Tag und zur selben Uhrzeit ein Termin folgende Trauerhalle aufweist:</div>
                <ComboBox
                    placeholder="Trauerhalle"
                    canRemoveSelection={true}
                    dataSource={mourningHalls}
                    dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                    comboBoxType={ComboBoxType.dropDownList}
                    valueMember="id"
                    displayMember="name"
                    size={InputSize.normal}
                    value={valueWhen && valueWhen.value}
                    selectedValue={valueWhen && valueWhen.selectedValue}
                    errorMessage={errors["WhenId"]}
                    onSelectionChanged={(data) => this.onComboBoxChanged(data, "when")}>
                    {
                        (item: IMourningHall) =>
                        {
                            return (
                                <DropDownTextItem
                                    key={item.id}
                                    value={item.id}
                                    displayValue={item.name} />
                            );
                        }
                    }
                </ComboBox>
            </div>
        );

        return when;
    }

    /**
     * Rendert die Kalender Snippets.
     */
    private renderCalendars(): JSX.Element[]
    {
        const { calendars, valueCalendars } = this.props;
        const elements: JSX.Element[] = [];

        if (valueCalendars != null)
        {
            for (let i = 0; i < valueCalendars.length; i++)
            {
                let text: string = elements.length > 0 ? "oder" : "und folgender Kalender zugewiesen ist:";
                elements.push(
                    <div className="mh-rule-editor_rule_snippet">
                        <div className="mh-rule-editor_rule_snippet_dot"></div>
                        <div className="mh-rule-editor_rule_snippet_text">{text}</div>
                        <div
                            className="mh-rule-editor_rule_snippet_remove"
                            title="Regel Snippet entfernen"
                            onClick={() => this.onRemoveSnippet("calendar", i)}>
                            <i className="mdi mdi-close"></i>
                        </div>
                        <ComboBox
                            placeholder="Kalender"
                            canRemoveSelection={true}
                            dataSource={calendars}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            valueMember="id"
                            displayMember="name"
                            size={InputSize.normal}
                            value={valueCalendars[i].value}
                            selectedValue={valueCalendars[i].selectedValue}
                            onSelectionChanged={(selectedItem) => this.onCalendarChanged(selectedItem, i)}>
                            {
                                (item: ICalendar) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.name} />
                                    );
                                }
                            }
                        </ComboBox>
                    </div>
                );
            }
        }

        return elements;
    }

    /**
     * Rendert das "Dann" Snippet.
     */
    private renderThen(): JSX.Element[]
    {
        const { mourningHalls, isThenVisible, valueThen, errors } = this.props;
        const then: JSX.Element[] = [];

        if (isThenVisible)
        {
            then.push(<div className="mh-rule-editor_rule_header">dann</div>);
            then.push(
                <div className="mh-rule-editor_rule_snippet">
                    <div className="mh-rule-editor_rule_snippet_dot"></div>
                    <div className="mh-rule-editor_rule_snippet_text">darf folgende Trauerhalle nicht verwendet werden:</div>
                    <div
                        className="mh-rule-editor_rule_snippet_remove"
                        title="Regel Snippet entfernen"
                        onClick={() => this.onRemoveSnippet("then")}>
                        <i className="mdi mdi-close"></i>
                    </div>
                    <ComboBox
                        placeholder="Trauerhalle"
                        canRemoveSelection={true}
                        dataSource={mourningHalls}
                        dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                        comboBoxType={ComboBoxType.dropDownList}
                        valueMember="id"
                        displayMember="name"
                        size={InputSize.normal}
                        value={valueThen && valueThen.value}
                        selectedValue={valueThen && valueThen.selectedValue}
                        errorMessage={errors["ThenId"]}
                        onSelectionChanged={(data) => this.onComboBoxChanged(data, "then")}>
                        {
                            (item: IMourningHall) =>
                            {
                                return (
                                    <DropDownTextItem
                                        key={item.id}
                                        value={item.id}
                                        displayValue={item.name} />
                                );
                            }
                        }
                    </ComboBox>
                </div>
            );

            return then;
        }
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const { mourningHalls, isThenVisible, calendars } = this.props;
        
        return (
            <div className="mh-rule-editor">
                <div className="mh-rule-editor_actions">
                    <span className="mh-rule-editor_actions_text">Regel</span>
                </div>
                <div className="mh-rule-editor_rule">
                    {this.renderWhen()}
                    {this.renderCalendars()}
                    {isThenVisible && this.renderButtons()}
                    {this.renderThen()}
                    {!isThenVisible && this.renderButtons()}
                </div>
            </div>
        );
    }
}